<script lang="ts">
  import { onMount } from "svelte";
  import { Cross, FilledArrowUp } from "../icons/index.js";
  import Button from "./Button.svelte";
  import { slide } from "svelte/transition";
  import { quintOut } from "svelte/easing";

  interface FileMeta {
    name: string;
    size: number;
    type: string;
    lastModified: number;
  }

  export type SupportedFileType = "csv" | "txt" | "xls" | "xlsx";

  interface FileImporterProps {
    supportedFileTypes?: SupportedFileType[];
    maxFiles?: number;
    storageKey?: string;
    onUpload?: (files: File[]) => void;
    onRemove?: (index: number) => void;
    validated?: boolean;
  }

  let {
    supportedFileTypes = ["csv", "txt", "xls", "xlsx"],
    maxFiles = 10,
    storageKey = "uploadedFileData",
    onUpload,
    onRemove,
    validated = true,
  }: FileImporterProps = $props();

  let files: File[] = $state([]);
  let fileMetas: FileMeta[] = $state([]);
  let dragOver = $state(false);
  let fileInput: HTMLInputElement | null = $state(null);

  const fileTypeToMimeType = {
    csv: "text/csv",
    txt: "text/plain",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  } as const;

  const fileTypeToExtension = {
    csv: ".csv",
    txt: ".txt",
    xls: ".xls",
    xlsx: ".xlsx",
  } as const;

  const validTypes = $derived(supportedFileTypes.map((type) => fileTypeToMimeType[type]));

  const acceptedTypes = $derived(validTypes.join(","));
  const canAddMore = $derived(fileMetas.length < maxFiles);
  const fileTypeText = $derived.by(() => {
    const extensions = supportedFileTypes.map((type) => fileTypeToExtension[type]);
    return `Supported file types: ${extensions.join(", ")}`;
  });

  onMount(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        fileMetas = JSON.parse(stored);
      } catch (error) {
        console.warn("Failed to parse stored file data:", error);
        localStorage.removeItem(storageKey);
      }
    }

    const handleBeforeUnload = () => {
      localStorage.removeItem(storageKey);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  });

  $effect(() => {
    localStorage.setItem(storageKey, JSON.stringify(fileMetas));
  });

  function isValidFile(file: File): boolean {
    return validTypes.includes(file.type as any);
  }

  function createFileMeta(file: File): FileMeta {
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
    };
  }

  function isDuplicate(file: File): boolean {
    return fileMetas.some(
      (meta) => meta.name === file.name && meta.lastModified === file.lastModified,
    );
  }

  function handleFileSelection(selectedFiles: FileList | null): void {
    if (!selectedFiles || selectedFiles.length === 0) return;
    if (!canAddMore) {
      alert(`Maximum ${maxFiles} files allowed.`);
      return;
    }

    const fileArray = Array.from(selectedFiles);

    const invalidFile = fileArray.find((file) => !isValidFile(file));
    if (invalidFile) {
      alert("Please upload only supported file types.");
      return;
    }

    const newFiles = fileArray
      .filter((file) => !isDuplicate(file))
      .slice(0, maxFiles - fileMetas.length);

    if (newFiles.length === 0) {
      alert("No new files to add (duplicates or limit reached).");
      return;
    }

    if (onUpload && newFiles.length > 0) {
      onUpload(newFiles);
    }

    if (validated) {
      files = [...files, ...newFiles];
      fileMetas = [...fileMetas, ...newFiles.map(createFileMeta)];
    }
  }

  function removeFile(index: number): void {
    files = files.filter((_, i) => i !== index);
    fileMetas = fileMetas.filter((_, i) => i !== index);
    onRemove?.(index);
  }

  function handleFileInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    handleFileSelection(target.files);
    target.value = "";
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if ((event.key === "Enter" || event.key === " ") && canAddMore) {
      event.preventDefault();
      fileInput?.click();
    }
  }

  function handleDragOver(event: DragEvent): void {
    event.preventDefault();
    dragOver = true;
  }

  function handleDragLeave(): void {
    dragOver = false;
  }

  function handleDrop(event: DragEvent): void {
    event.preventDefault();
    dragOver = false;
    handleFileSelection(event.dataTransfer?.files ?? null);
  }

  function triggerFileInput(): void {
    if (canAddMore) {
      fileInput?.click();
    }
  }
</script>

<div class="flex flex-col gap-1.5">
  <div
    class="group flex cursor-pointer flex-col items-center
           justify-center gap-2 rounded-2xl border border-dashed border-alpha-100 bg-alpha-025
           p-2 py-6 transition-colors duration-200 ease-out hover:border-alpha-100 hover:bg-alpha-050"
    class:bg-alpha-100={dragOver}
    class:opacity-50={!canAddMore}
    class:cursor-not-allowed={!canAddMore}
    onclick={triggerFileInput}
    ondragover={handleDragOver}
    ondragleave={handleDragLeave}
    ondrop={handleDrop}
    role="button"
    tabindex="0"
    onkeydown={handleKeyDown}
    aria-label={canAddMore ? "Upload file" : `Maximum ${maxFiles} files reached`}
    aria-disabled={!canAddMore}
  >
    <input
      bind:this={fileInput}
      type="file"
      accept={acceptedTypes}
      multiple
      class="hidden"
      onchange={handleFileInputChange}
      disabled={!canAddMore}
    />

    <div class="flex flex-col items-center gap-3">
      <div
        class="flex h-7 w-7 items-center justify-center rounded-full bg-mono-100 shadow-border-inset"
      >
        <div class="group-hover:animate-miniBounce flex">
          <FilledArrowUp size={16} class="text-foreground-tertiary" />
        </div>
      </div>
      <p class="text-sm text-foreground-tertiary">
        {canAddMore ? "Drop files here or click to upload" : `Maximum ${maxFiles} files reached`}
      </p>
    </div>
  </div>

  <p class="text-xs text-foreground-tertiary">{fileTypeText}.</p>

  {#if fileMetas.length > 0}
    <div class="mt-2 space-y-2">
      {#each fileMetas as file, index (file.name + file.lastModified)}
        <div
          in:slide={{ duration: 500, easing: quintOut }}
          out:slide={{ duration: 300, easing: quintOut }}
          class="mt-1 flex items-center justify-between gap-2 rounded-xl bg-alpha-025 px-4 py-2"
        >
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm text-foreground-secondary" title={file.name}>
              {file.name}
            </p>
            <p class="text-xs text-foreground-tertiary">
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <Button
            variant="secondary"
            size="xs"
            shape="square"
            class="shrink-0 text-alpha-500 transition-colors ease-out hover:text-foreground-primary"
            onclick={() => removeFile(index)}
            aria-label="Remove {file.name}"
          >
            <Cross size={14} />
          </Button>
        </div>
      {/each}
    </div>
  {/if}
</div>
